import React, { useState, useEffect } from 'react';
import {
  Snackbar,
  Box,
  Typography,
  Button,
  IconButton,
  Paper
} from '@mui/material';
import {
  GetApp as InstallIcon,
  Close as CloseIcon,
  PhoneIphone as PhoneIcon
} from '@mui/icons-material';

const DISMISSED_KEY = 'pwa_install_dismissed_until';
const DISMISS_DURATION_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const PWAInstallBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Don't show if already dismissed recently
    const dismissedUntil = localStorage.getItem(DISMISSED_KEY);
    if (dismissedUntil && Date.now() < Number(dismissedUntil)) {
      return;
    }

    // Don't show if already running as standalone PWA
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    const installedHandler = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setVisible(false);
      setDeferredPrompt(null);
    }
    // If dismissed in the OS prompt, keep our banner hidden for the session
    // but don't write localStorage — let them see it again next session
  };

  const handleDismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISSED_KEY, String(Date.now() + DISMISS_DURATION_MS));
  };

  if (!visible) return null;

  return (
    <Snackbar
      open={visible}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{
        bottom: { xs: 80, sm: 24 }, // clear the SOS FAB on mobile
        left: '50%',
        transform: 'translateX(-50%)',
        width: { xs: 'calc(100% - 32px)', sm: 'auto' },
        maxWidth: 480
      }}
    >
      <Paper
        elevation={6}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.5,
          bgcolor: 'grey.900',
          border: '1px solid rgba(255,255,255,0.12)',
          borderLeft: '4px solid',
          borderLeftColor: 'primary.main',
          width: '100%'
        }}
      >
        <PhoneIcon sx={{ color: 'primary.main', flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            Add FamilyForLife to Home Screen
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Install for quick access — works offline
          </Typography>
        </Box>
        <Button
          size="small"
          variant="contained"
          startIcon={<InstallIcon />}
          onClick={handleInstall}
          sx={{ flexShrink: 0, textTransform: 'none' }}
        >
          Install
        </Button>
        <IconButton size="small" onClick={handleDismiss} sx={{ flexShrink: 0, color: 'text.secondary' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Paper>
    </Snackbar>
  );
};

export default PWAInstallBanner;
